import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Mail } from 'lucide-react'
import { api } from '@/lib/api'

/**
 * OPT-043：设置页"邮件测试"卡片——输入收件邮箱发一封测试邮件，验证邮件服务连通性。
 * 凭据缺失时后端返回 sent:false + reason:'EMAIL_NOT_CONFIGURED'，此处展示"未配置"提示（不假成功）。
 * 失败原因（如通道方报错）不透传给教练——只显示通用失败文案，避免暴露内部设施细节。
 */
export function EmailTestCard() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [result, setResult] = useState<'success' | 'failed' | 'notConfigured' | null>(null)

  const send = useMutation({
    mutationFn: (to: string) => api.sendTestEmail(to),
    onSuccess: (res) => {
      if (res.sent) {
        setResult('success')
      } else if (res.reason === 'EMAIL_NOT_CONFIGURED') {
        setResult('notConfigured')
      } else {
        setResult('failed')
      }
    },
    onError: () => setResult('failed'),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setResult(null)
    send.mutate(email.trim())
  }

  return (
    <Card data-testid="email-test-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-4 w-4" /> {t('settings.emailTest.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">{t('settings.emailTest.description')}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="email-test-to">{t('settings.emailTest.emailLabel')}</Label>
            <Input
              id="email-test-to"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('settings.emailTest.emailPlaceholder')}
              required
              data-testid="email-test-input"
            />
          </div>
          <Button
            type="submit"
            data-testid="email-test-send"
            disabled={send.isPending || !email.trim()}
          >
            {send.isPending ? t('settings.emailTest.sending') : t('settings.emailTest.sendButton')}
          </Button>
        </form>
        {result === 'success' && (
          <p className="text-sm text-green-600" data-testid="email-test-success">
            {t('settings.emailTest.success')}
          </p>
        )}
        {result === 'notConfigured' && (
          <p className="text-sm text-amber-600" data-testid="email-test-not-configured">
            {t('settings.emailTest.notConfigured')}
          </p>
        )}
        {result === 'failed' && (
          <p className="text-sm text-red-600" data-testid="email-test-failed">
            {t('settings.emailTest.failed')}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
